import type { ReactNode } from "react";

/**
 * Forms open as the canvas's right-hand sheet (bottom sheet on phones); short confirmations
 * (width under 440) stay a small centred dialog. Stacks above an open Drawer.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
  width = 480,
  subtitle,
}: {
  title: string;
  /** One line under the title. */
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const kind = width < 440 ? "o-dialog--center" : "o-dialog--side";
  return (
    <div className={`o-scrim ${kind}`} style={{ zIndex: 300 }} onClick={onClose}>
      <section className="o-dialog" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()} style={{ ["--w" as string]: `${Math.max(width, 500)}px` }}>
        <header className="o-dialog__head">
          <h2>
            {title}
            {subtitle && <small style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--text2)", letterSpacing: 0, marginTop: 4 }}>{subtitle}</small>}
          </h2>
          <button type="button" aria-label="Close" className="o-close" onClick={onClose}>
            <span className="ms" aria-hidden="true">close</span>
          </button>
        </header>
        <div className="o-dialog__body">{children}</div>
        {footer && <footer className="o-dialog__foot">{footer}</footer>}
      </section>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="o-field">
      <div className="o-field__label">{label}</div>
      {children}
    </div>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`o-input ${props.className ?? ""}`} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`o-input o-input--area ${props.className ?? ""}`} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`o-input ${props.className ?? ""}`} />;
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} className="o-toggle" onClick={() => onChange(!checked)}>
      <span className="o-toggle__track" aria-hidden="true" />
      {label && <span className="o-toggle__label">{label}</span>}
    </button>
  );
}
